/* eslint-disable max-len */
import i18n from '@i18next/i18n';
import type { IClipBoardReadData, IClipBoardWriteData } from '@tencent/clipboard';
import type {
    IClipboardProtectReadOption,
    IClipboardProtectWriteOption,
} from '@tencent/clipboard/dist/common/clipboard-protect-option';
import ua from '@tencent/docs-user-agent';
import Snackbar from '@tencent/dui/lib/components/Snackbar';
import { ClipboardType } from '../../types';
import { AdapterClipboardReadParams, AdapterClipboardWriteParams } from '../clipboard-delegate';

// eslint-disable-next-line import/no-mutable-exports
export let IS_IN_TENCENTCLIPBOARD_COPY_STATUS = false;

export class TencentClipboard {
    private static instance: TencentClipboard;

    public static getInstance(): TencentClipboard {
        if (!TencentClipboard.instance) {
            TencentClipboard.instance = new TencentClipboard();
        }
        return TencentClipboard.instance;
    }

    private concreteClipboard: {
        readInClipBoard: (option: IClipboardProtectReadOption) => Promise<IClipBoardReadData>;
        writeInClipBoard: (originContent: IClipBoardWriteData, option?: IClipboardProtectWriteOption) => Promise<void>;
    };

    public read: (option: IClipboardProtectReadOption) => Promise<IClipBoardReadData | undefined> = async (args) => {
        try {
            if (!this.concreteClipboard) {
                this.concreteClipboard = await this.getTencentClipboard();
                // 经过验证，如果直接粘贴外部内容，需要再次尝试粘贴操作才能粘贴进来
                // 所以在这里统一给出提示
                Snackbar.show({
                    message: i18n.t('请再次尝试粘贴操作'),
                });
            }

            const { readFile } = args as AdapterClipboardReadParams;

            if (readFile) {
                return this.concreteClipboard.readInClipBoard(args).then(res => res.files) as any;
            }

            return this.concreteClipboard.readInClipBoard(args);
        } catch (error) {
            window.log.error('Failed to read from clipboard: ', error);
            return Promise.resolve(undefined);
        }
    };

    // eslint-disable-next-line complexity
    public write: (originContent: IClipBoardWriteData, option?: IClipboardProtectWriteOption) => Promise<void> = async (...args) => {
        try {
            if (!this.concreteClipboard) {
                this.concreteClipboard = await this.getTencentClipboard();
                if (ua.isFirefox || ua.isSafari) {
                    Snackbar.show({
                        message: i18n.t('请再次尝试复制操作'),
                    });
                }
            }
            IS_IN_TENCENTCLIPBOARD_COPY_STATUS = true;
            const { html, plain, syncClipboardItemConf, asyncClipboardItemConf } = args[0] as AdapterClipboardWriteParams;
            await this.concreteClipboard.writeInClipBoard(
                {
                    html: html ?? syncClipboardItemConf?.[ClipboardType.HTML] ?? '',
                    plain: plain ?? syncClipboardItemConf?.[ClipboardType.TEXT] ?? '',
                    blobAsync: () => Promise.resolve(asyncClipboardItemConf?.[ClipboardType.IMAGE_PNG] || new Blob()),
                    blobType: asyncClipboardItemConf?.[ClipboardType.IMAGE_PNG] ? ClipboardType.IMAGE_PNG : undefined,
                },
                args[1],
            );
            IS_IN_TENCENTCLIPBOARD_COPY_STATUS = false;
            Promise.resolve();
        } catch (error) {
            IS_IN_TENCENTCLIPBOARD_COPY_STATUS = false;
            window.log.error('Failed to write to clipboard: ', error);
            Promise.resolve();
        }
    };

    private getTencentClipboard() {
        return import(/* webpackChunkName: "tencent-clipboard" */ '@tencent/clipboard');
    }
}
